import olView from "ol/View";
import type {ViewConfiguration} from "./configuration/view"
import GIS from "./app"
import {isEmpty} from "ol/extent";
import {fromExtent} from 'ol/geom/Polygon';
import {transformExtent} from 'ol/proj';
import {getPointResolution as olGetPointResolution} from 'ol/proj';
import WKT from "ol/format/WKT";
import {XY, Rotation, Zoom, ScaleOrPointResolutionCallback, GetExtent} from "./general/callback";
import {degreesToRadians, radiansToDegrees} from "./general/function";
import {ExtentOptions, ZoomOptions, Listener} from "./general/options";

class View {
    private readonly GIS;
    public delay = 500;
    public firstLoading:boolean = true;
    public loadingStart:ReturnType<typeof setTimeout> | null = null;
    public loadingEnd:ReturnType<typeof setTimeout> | null = null;
    public listeners:Listener = {};
    public olObject!:olView;
    constructor(GIS: GIS) {
        this.GIS = GIS;
    }

    public init(configuration:ViewConfiguration) {
        this.olObject = new olView({
            center: configuration.center,
            zoom: configuration.zoom,
            projection: this.GIS.Projection.getRegistered(configuration.epsg),
            extent: configuration.extent
        })

        this.olObject.set('maxExtent', configuration.extent);
    }

    /**
     * Rotate map
     * @param degrees
     * @example Map.rotate(35.1)
     */
    public rotate(degrees:number) {
        const radians = degreesToRadians(degrees);
        this.olObject.setRotation(radians);
    }
    /**
     * Return center coordinates values.
     * @param callback - Callback with center coordinate value.
     * @returns Center coordinates [X, Y]
     */
    public getCenter(callback?: XY) {
        const center = this.olObject.getCenter() || [0,0];

        if (callback) {
            callback(center[0], center[1])
        }

        return [center[0], center[1]];
    }
    /**
     * Get current rotation in degrees and radians.
     * @param callback - Callback with degrees and radians.
     * @returns Rotation values {degrees: X, radians: X}
     */
    public getRotation(callback?:Rotation) {
        const radians = this.olObject.getRotation();
        const degrees = radiansToDegrees(radians);

        if (callback && degrees && radians) {
            callback(degrees, radians)
        }

        return { degrees, radians };
    }
    /**
     * Get zoom level as an integer.
     * @param callback - Callback with zoom level.
     * @return Zoom level
     */
    public getZoom(callback?:Zoom | null) {
        const level = this.olObject.getZoom();

        if (callback && level) {
            callback(level)
        }

        return level;
    }
    /**
     * Set zoom level as an integer.
     * @param zoom - Zoom level to zoom in/out. {@link https://openlayers.org/en/latest/apidoc/module-ol_View-View.html#setZoom}
     */
    public setZoom (zoom: number) {
        return this.olObject.setZoom(zoom);
    }

    /**
     * Get scale as an integer.
     * @return Scale
     */
    public getScale(callback?:ScaleOrPointResolutionCallback) {
        const resolution = this.getPointResolution();
        //The standard WMS 1.3.0 specifies the pixel width : 0.28 mm.
        //1 inch is 25.4 mm
        const dpi = 25.4 / 0.28;
        const inchesPerMeter = 1000 / 25.4;
        const scale = resolution * inchesPerMeter * dpi;
        if (callback) {
            callback(scale);
        }

        return scale;
    }
    /**
     * @deprecated since version 1.1 - use {@link getExtent} instead
     * This method is deprecated and will be removed in future releases.
     */
    public getViewExtent (callback?: GetExtent, epsg?: number, options?: ExtentOptions) {
        console.warn('getViewExtent is deprecated. Use getExtent instead. This method will be removed in future releases.');

        return this.getExtent(callback, epsg, options);
    };
    /**
     * Get view extent.
     * @param callback
     * @param epsg - code of a coordinate system {@link https://epsg.io/}
     * @param options - specify e.g. output format
     * @returns Extent array [minx, miny, maxx, maxy]
     * @throws Unknown extent format.
     */
    public getExtent(callback?:GetExtent, epsg?:number, options?:ExtentOptions) {
        let extent = this.olObject.calculateExtent();
        const sourceProjection = this.olObject.getProjection();

        let targetProjection;
        if (epsg) {
            targetProjection = this.GIS.Projection.getRegistered(epsg);

            extent = transformExtent(extent, sourceProjection, targetProjection);
        }

        let result:import("ol/extent.js").Extent | string = extent

        if (options && options.format) {
            switch (options.format) {
                case this.GIS.Layer.format.WKT:
                    const geom = fromExtent(extent);
                    result = new WKT().writeGeometry(geom);
                    if (callback) {
                        callback(result);
                    }
                    break;
                default:
                    throw new Error('Unknown extent format.');
            }
        } else if (callback) {
            callback(extent[0], extent[1], extent[2], extent[3]);
        }

        return result;
    }
    /**
     * Get point resolution as an integer.
     * @param callback
     * @param epsg - EPSG code as an integer.
     * @return Point resolution
     * @throws EPSG code is not registered.
     */
    public getPointResolution(callback?:ScaleOrPointResolutionCallback, epsg?: number) {
        let projection = this.olObject.getProjection();
        const resolution = this.olObject.getResolution();
        const center = this.olObject.getCenter();
        if (epsg) {
            projection = this.GIS.Projection.getRegistered(epsg);
        }

        if (resolution === undefined) {
            throw Error('Could not get resolution.');
        }

        if (center === undefined) {
            throw Error('Could not get center.');
        }

        if (callback) {
            callback(olGetPointResolution(projection, resolution, center))
        }

        return olGetPointResolution(projection, resolution, center);
    }

    /**
     * Zoom to defined extent
     * @param extent - {@link https://openlayers.org/en/latest/apidoc/module-ol_extent.html#~Extent}
     * @param maxZoom - {@link https://openlayers.org/en/latest/apidoc/module-ol_View-View.html#fit}
     * @param options - {@link ZoomOptions} - duration, padding, epsg; default padding is [0, 0, 0, 0] and duration is 0
     */
    public zoomToExtent(extent: import("ol/extent.js").Extent, maxZoom?: number | undefined, options?: ZoomOptions) {
        let transformedExtent = extent;
        let padding = [0, 0, 0, 0];
        let duration = 0;

        if (isEmpty(extent) || extent.length !== 4) {
            throw new Error('Empty extent.');
        }

        if (options && options.epsg) {
            transformedExtent = transformExtent(extent, this.GIS.Projection.getRegistered(options.epsg), this.olObject.getProjection());
        }

        if (!maxZoom) {
            maxZoom = undefined;
        }

        if (options && options.padding) {
            padding = options.padding;
        }

        if (options && options.duration) {
            duration = options.duration;
        }

        this.olObject.fit(transformedExtent, {maxZoom: maxZoom, padding, duration});
    }
}

export default View;